import React, { useState, useEffect } from 'react';
import { Truck, MapPin, Clock, Check, Package, RotateCcw, Gift } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * DeliverySelector v2 — Livraison + Reprise UNIQUEMENT
 * Zones officielles (barème canonique §1.4) :
 *   • 0–20 km  : Gratuit
 *   • 20–45 km : 20€ forfait
 *   • > 45 km  : 45€ forfait
 * Props:
 *   onDeliveryChange  : callback({ tarif_total, option_label, zone_label, ... })
 *   externalZone      : zone_id détecté par ZoneChecker
 *   nbArticles        : nombre d'articles dans le panier
 */
export default function DeliverySelector({ onDeliveryChange, externalZone = null, nbArticles = 1 }) {
  const [selectedZone, setSelectedZone] = useState('zone_1');
  const [calcResult, setCalcResult] = useState(null);
  const [zones, setZones] = useState({});
  const [loading, setLoading] = useState(false);

  // Charger les zones
  useEffect(() => {
    fetch(`${BACKEND}/api/livraison/options`)
      .then(r => r.json())
      .then(d => setZones(d.zones || {}))
      .catch(() => {});
  }, []);

  // Synchroniser avec ZoneChecker
  useEffect(() => {
    if (externalZone && externalZone !== selectedZone) {
      setSelectedZone(externalZone);
    }
  // eslint-disable-next-line
  }, [externalZone]);

  // Recalculer à chaque changement de zone
  useEffect(() => {
    calculateDelivery(selectedZone);
  // eslint-disable-next-line
  }, [selectedZone]);

  async function calculateDelivery(zone_id) {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/livraison/calculate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ option: 'livraison_reprise', zone_id }),
      });
      const data = await res.json();
      setCalcResult(data);
      if (onDeliveryChange) onDeliveryChange(data);
    } catch { /* eslint-disable-line no-empty */ }
    setLoading(false);
  }

  // Zones filtrées (exclure hors zone dans le select)
  const zonesSelect = Object.entries(zones).filter(([k]) => k !== 'zone_hors');

  const isGratuit   = calcResult?.tarif_total === 0;
  const prix        = calcResult?.tarif_total || 0;

  // Libellés des zones simplifiés
  const ZONE_LABELS = {
    zone_1: '0 – 20 km — Livraison offerte 🎁',
    zone_2: '20 – 45 km — 20€',
    zone_3: 'Au-delà de 45 km — 45€',
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <RotateCcw size={15} style={{ color: 'var(--teal)' }} />
          <p className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>Transport</p>
        </div>
        <span className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium"
          style={{ background: 'rgba(93,188,191,0.08)', color: '#0B4B4E' }}>
          <Package size={11} />
          Forfait pour tout le panier ({nbArticles} article{nbArticles > 1 ? 's' : ''})
        </span>
      </div>

      {/* Option unique : Livraison + Reprise (affiché comme info, non cliquable) */}
      <div className="flex items-start gap-3 p-3 rounded-xl mb-4"
        style={{ background: 'var(--teal-mist)', border: '1.5px solid var(--teal)', boxShadow: '0 4px 12px rgba(93,188,191,0.15)' }}>
        <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
          style={{ background: 'var(--teal)', color: '#0B1E20' }}>
          <RotateCcw size={18} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>
              Livraison + Reprise
            </p>
            <span className="text-xs px-2 py-0.5 rounded-full"
              style={{ background: 'var(--teal-mist)', color: '#0B4B4E' }}>
              Inclus
            </span>
          </div>
          <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
            Livraison, installation et reprise après l'événement. Tout inclus.
          </p>
        </div>
      </div>

      {/* Sélecteur de zone */}
      <div className="mb-3">
        <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--ink)' }}>
          <MapPin size={12} className="inline mr-1" style={{ color: 'var(--teal)' }} />
          Votre distance depuis Figeac
        </label>
        <select
          value={selectedZone}
          onChange={e => setSelectedZone(e.target.value)}
          className="w-full px-3 py-2.5 rounded-xl text-sm"
          style={{ background: '#FFFFFF', border: '1px solid var(--border)', color: 'var(--ink)', outline: 'none' }}>
          {zonesSelect.map(([zId]) => (
            <option key={zId} value={zId}>
              {ZONE_LABELS[zId] || zId}
            </option>
          ))}
        </select>
        {externalZone && externalZone === selectedZone && (
          <p className="text-xs mt-1 flex items-center gap-1" style={{ color: '#1F9D7A' }}>
            <Check size={11} /> Zone détectée automatiquement depuis votre adresse
          </p>
        )}
      </div>

      {/* Résultat */}
      {calcResult && (
        <div className="p-3 rounded-xl"
          style={{
            background: isGratuit ? 'rgba(31,157,122,0.08)' : 'var(--teal-mist)',
            border: isGratuit ? '1.5px solid rgba(31,157,122,0.4)' : '1px solid rgba(93,188,191,0.2)',
          }}>
          {isGratuit && (
            <div className="flex items-center gap-2 mb-2 px-2.5 py-1.5 rounded-lg"
              style={{ background: 'rgba(31,157,122,0.12)' }}>
              <Gift size={14} style={{ color: '#1F9D7A' }} />
              <span className="text-sm font-bold" style={{ color: '#1F9D7A' }}>
                Livraison offerte — vous êtes dans les 20 km de Figeac 🎉
              </span>
            </div>
          )}
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <Check size={14} style={{ color: isGratuit ? '#1F9D7A' : 'var(--teal)' }} />
              <span className="text-sm font-semibold" style={{ color: '#0B4B4E' }}>
                Livraison + Reprise
              </span>
            </div>
            <span className="text-base font-bold"
              style={{ color: isGratuit ? '#1F9D7A' : 'var(--gold, #C58B19)' }}>
              {loading ? '…' : isGratuit ? 'Gratuit 🎁' : `+${prix}€`}
            </span>
          </div>
          {calcResult.zone_label && (
            <p className="text-xs" style={{ color: 'rgba(11,75,78,0.7)' }}>{calcResult.zone_label}</p>
          )}
          <p className="text-xs mt-1.5 font-medium" style={{ color: '#0B4B4E', opacity: 0.8 }}>
            ✅ Ce forfait couvre <strong>tous vos articles</strong> en une seule tournée
          </p>
        </div>
      )}

      {/* Règle retour */}
      <div className="flex items-start gap-2 p-2.5 rounded-xl mt-2"
        style={{ background: 'rgba(197,139,25,0.06)', border: '1px solid rgba(197,139,25,0.15)' }}>
        <Clock size={12} style={{ color: '#C58B19', flexShrink: 0, marginTop: 1 }} />
        <p className="text-xs" style={{ color: '#C58B19' }}>
          Retour du matériel : <strong>le lendemain avant 10H00 maximum</strong>
        </p>
      </div>
    </div>
  );
}
